/**
 * Pure canonical-data helpers for Rogue combat features.
 *
 * These helpers inspect embedded feature/action records without parsing rules
 * text. Runtime mechanics are selected through explicit structured fields.
 * rule_reference: 2024 PHB Rogue — Sneak Attack, Cunning Strike, Uncanny Dodge.
 * rule_reference: AGENTS.md — canonical data only; no runtime text inference.
 * date_of_change: 2026-07-18
 */

export const CUNNING_STRIKE_EFFECTS = Object.freeze({
  poison: Object.freeze({ key: 'poison', label: 'Poison', diceCost: 1, saveAbility: 'constitution' }),
  trip: Object.freeze({ key: 'trip', label: 'Trip', diceCost: 1, saveAbility: 'dexterity' }),
  withdraw: Object.freeze({ key: 'withdraw', label: 'Withdraw', diceCost: 1, saveAbility: null })
});

const canonicalSources = (entity) => [
  entity,
  entity?.entity,
  entity?.entityPayload,
  entity?.characterData,
  entity?.canonicalData,
  entity?.data,
  entity?.monsterData,
  entity?.monsterData?.data
].filter((source) => source && typeof source === 'object');

const featureEntries = (entity) => canonicalSources(entity).flatMap((source) => [
  source.features,
  source.classFeatures,
  source.traits,
  source.specialAbilities,
  source.reactions,
  source.actions?.reactions
]).flatMap((basket) => (
  Array.isArray(basket) ? basket : (Array.isArray(basket?.list) ? basket.list : [])
));

export const hasNamedCombatFeature = (entity, featureName) => {
  const wanted = String(featureName || '').trim().toLowerCase();
  if (!wanted) return false;
  return featureEntries(entity).some((feature) => (
    String(feature?.name ?? feature?.title ?? feature).trim().toLowerCase() === wanted
  ));
};

export const normalizeCunningStrikeSelection = (selection) => {
  if (selection == null) return { valid: true, selection: null };
  if (!selection || typeof selection !== 'object') {
    return { valid: false, error: 'actionData.cunningStrike must be an object or null.' };
  }
  const effect = String(selection.effect || '').trim().toLowerCase();
  const rule = CUNNING_STRIKE_EFFECTS[effect];
  if (!rule) {
    return {
      valid: false,
      error: `actionData.cunningStrike.effect must be one of: ${Object.keys(CUNNING_STRIKE_EFFECTS).join(', ')}.`
    };
  }
  if (selection.diceCost != null && Number(selection.diceCost) !== rule.diceCost) {
    return { valid: false, error: `Cunning Strike ${rule.label} costs exactly ${rule.diceCost}d6.` };
  }
  return { valid: true, selection: { ...rule } };
};

export const resolveExplicitAbilityScore = (entity, ability) => {
  for (const source of canonicalSources(entity)) {
    const score = source?.abilities?.[ability];
    if (typeof score === 'number' && Number.isFinite(score)) return score;
  }
  return null;
};

export const resolveExplicitProficiencyBonus = (entity) => {
  for (const source of canonicalSources(entity)) {
    for (const value of [source.proficiencyBonus, source.profBonus, source.combatStats?.proficiencyBonus]) {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
    }
  }
  return null;
};

export const resolveSavingThrowModifier = (entity, ability) => {
  const score = resolveExplicitAbilityScore(entity, ability);
  if (!Number.isFinite(score)) {
    return { valid: false, error: `Target is missing canonical abilities.${ability}.` };
  }
  const abilityModifier = Math.floor((score - 10) / 2);
  for (const source of canonicalSources(entity)) {
    const saves = source.savingThrows;
    if (!saves || typeof saves !== 'object' || Array.isArray(saves)) continue;
    const value = saves[ability];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return { valid: true, modifier: value, abilityModifier, proficient: value !== abilityModifier };
    }
    if (typeof value === 'boolean') {
      if (!value) return { valid: true, modifier: abilityModifier, abilityModifier, proficient: false };
      const proficiencyBonus = resolveExplicitProficiencyBonus(entity);
      if (!Number.isFinite(proficiencyBonus)) {
        return { valid: false, error: `Target is proficient in ${ability} saves but is missing canonical proficiencyBonus.` };
      }
      return {
        valid: true,
        modifier: abilityModifier + proficiencyBonus,
        abilityModifier,
        proficient: true
      };
    }
  }
  return { valid: true, modifier: abilityModifier, abilityModifier, proficient: false };
};

export const resolveCunningStrikeSaveDC = (attacker) => {
  const dexterity = resolveExplicitAbilityScore(attacker, 'dexterity');
  const proficiencyBonus = resolveExplicitProficiencyBonus(attacker);
  if (!Number.isFinite(dexterity)) {
    return { valid: false, error: 'Cunning Strike requires canonical abilities.dexterity.' };
  }
  if (!Number.isFinite(proficiencyBonus)) {
    return { valid: false, error: 'Cunning Strike requires canonical proficiencyBonus.' };
  }
  return {
    valid: true,
    dc: 8 + Math.floor((dexterity - 10) / 2) + proficiencyBonus
  };
};

export const canTripSize = (size) => {
  const normalized = String(size || '').trim().toLowerCase();
  const index = ['tiny', 'small', 'medium', 'large', 'huge', 'gargantuan'].indexOf(normalized);
  if (index < 0) return { valid: false, error: 'Cunning Strike Trip requires canonical target.size.' };
  return { valid: true, allowed: index <= 3, size: normalized };
};

export const hasConditionImmunity = (entity, condition) => {
  const wanted = String(condition || '').trim().toLowerCase();
  return canonicalSources(entity).some((source) => {
    const values = source.conditionImmunities ?? source.conditionImmunity ?? source.immunities?.conditions;
    return Array.isArray(values) && values.some((value) => (
      String(value?.name ?? value).trim().toLowerCase() === wanted
    ));
  });
};

